/**
 * @file ThemeContext.js
 * @description Provee la paleta de color y el modo claro/oscuro a toda la app.
 *
 * ⚠ TEMPORAL. Mientras se decide cuál de las tres paletas se queda.
 *
 * QUIÉN MANDA SOBRE QUÉ
 *   1. El superadmin fija, desde el backoffice, la paleta y el modo por defecto
 *      de la plataforma, y decide si los usuarios pueden cambiarlos.
 *   2. Si puede: gana la elección del usuario; si nunca eligió, hereda la global.
 *   3. Si no puede: manda la global y se ignora lo que el usuario tuviera
 *      guardado. No se borra — si el superadmin vuelve a abrir el selector, cada
 *      quien recupera lo suyo.
 *
 * Cómo se aplica: dos atributos en `<html>`.
 *   data-ev-palette="a|b|c"    → selecciona la paleta en `tokens.css`
 *   data-bs-theme="light|dark" → atributo NATIVO de Bootstrap 5.3, así que todos
 *                                sus componentes pasan a oscuro sin trabajo extra
 *
 * Dónde se guarda: la elección del usuario en `localStorage` (`evazard_theme`);
 * la global, en `app_settings` del backend. Nada toca datos de ningún negocio.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    THEME_STORAGE_KEY,
    DEFAULT_PALETTE,
    DEFAULT_MODE,
    isValidPalette,
    isValidMode,
} from './palettes';
import { fetchGlobalTheme, readCachedGlobalTheme, GLOBAL_FALLBACK } from './themeApi';

const ThemeContext = createContext(null);

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Lee la elección del usuario. Devuelve `null` en cada campo que nunca eligió,
 * para poder distinguir «quiere la paleta A» de «no ha elegido nada»: sin esa
 * diferencia, cambiar la paleta global no llegaría a nadie.
 */
export const readStoredTheme = () => {
    try {
        const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
        if (!raw) return { palette: null, mode: null };
        const parsed = JSON.parse(raw);
        return {
            palette: isValidPalette(parsed?.palette) ? parsed.palette : null,
            mode: isValidMode(parsed?.mode) ? parsed.mode : null,
        };
    } catch {
        return { palette: null, mode: null };
    }
};

const writeStoredTheme = (value) => {
    try {
        window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(value));
    } catch {
        /* Modo privado o almacenamiento lleno: la preferencia dura la sesión. */
    }
};

/** ¿El sistema operativo pide oscuro? `matchMedia` no existe en jsdom antiguo. */
const systemPrefersDark = () => {
    try {
        return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
    } catch {
        return false;
    }
};

/** Traduce el modo elegido al tema que realmente se pinta. */
export const resolveMode = (mode, prefersDark) => {
    if (mode === 'system') return prefersDark ? 'dark' : 'light';
    return mode === 'dark' ? 'dark' : 'light';
};

/**
 * Aplica la regla de precedencia. Aislada a propósito: es la única lógica con
 * aristas de todo el módulo, y así se puede probar sin montar React.
 */
export const resolveEffective = (global, user) => {
    const bloqueado = global.allow_user_override === false;
    return {
        palette: bloqueado ? global.palette : (user.palette || global.palette),
        mode: bloqueado ? global.mode : (user.mode || global.mode),
        canChoose: !bloqueado,
    };
};

/**
 * Escribe los atributos en `<html>` y sincroniza el `theme-color` del navegador,
 * que es el que pinta la barra superior en móvil.
 */
export const applyThemeToDocument = (palette, resolved) => {
    const root = document.documentElement;
    root.setAttribute('data-ev-palette', palette);
    root.setAttribute('data-bs-theme', resolved);

    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
        const canvas = getComputedStyle(root).getPropertyValue('--ev-bg-canvas').trim();
        if (canvas) meta.setAttribute('content', canvas);
    }
};

export const ThemeProvider = ({ children }) => {
    // Arrancamos con lo último que supimos del servidor, no con el valor por
    // defecto: si el superadmin ocultó el selector, no queremos mostrarlo medio
    // segundo y luego quitarlo.
    const [global, setGlobal] = useState(() => readCachedGlobalTheme() || GLOBAL_FALLBACK);
    const [user, setUser] = useState(readStoredTheme);
    const [prefersDark, setPrefersDark] = useState(systemPrefersDark);

    // Seguir al sistema operativo mientras el modo sea `system`.
    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined;
        const mq = window.matchMedia(DARK_QUERY);
        const onChange = (event) => setPrefersDark(event.matches);
        // Safari < 14 solo tiene addListener.
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else if (mq.addListener) mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else if (mq.removeListener) mq.removeListener(onChange);
        };
    }, []);

    // Refrescar la configuración global una vez por carga.
    useEffect(() => {
        let vivo = true;
        fetchGlobalTheme().then((remoto) => {
            // `null` = no se pudo consultar. Nos quedamos con la caché: es
            // preferible un color desactualizado a repintar la app al azar.
            if (vivo && remoto) setGlobal(remoto);
        });
        return () => { vivo = false; };
    }, []);

    const efectivo = resolveEffective(global, user);
    const resolvedMode = resolveMode(efectivo.mode, prefersDark);

    useEffect(() => {
        applyThemeToDocument(efectivo.palette, resolvedMode);
    }, [efectivo.palette, resolvedMode]);

    const guardar = useCallback((parche) => {
        setUser((actual) => {
            const siguiente = { ...actual, ...parche };
            writeStoredTheme(siguiente);
            return siguiente;
        });
    }, []);

    const setPalette = useCallback((next) => {
        if (!isValidPalette(next)) return;
        guardar({ palette: next });
    }, [guardar]);

    const setMode = useCallback((next) => {
        if (!isValidMode(next)) return;
        guardar({ mode: next });
    }, [guardar]);

    /** Atajo del interruptor rápido: alterna entre claro y oscuro explícitos. */
    const toggleMode = useCallback(() => {
        setMode(resolvedMode === 'dark' ? 'light' : 'dark');
    }, [resolvedMode, setMode]);

    const value = useMemo(() => ({
        palette: efectivo.palette,
        mode: efectivo.mode,
        resolvedMode,
        isDark: resolvedMode === 'dark',
        canChoose: efectivo.canChoose,
        globalTheme: global,
        setPalette,
        setMode,
        toggleMode,
        setGlobalTheme: setGlobal,
    }), [efectivo.palette, efectivo.mode, efectivo.canChoose, resolvedMode, global,
        setPalette, setMode, toggleMode]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * Hook de consumo. Devuelve valores neutros si se usa fuera del provider, para
 * que ningún componente reviente por el orden de montaje ni en los tests.
 */
export const useTheme = () => {
    const ctx = useContext(ThemeContext);
    if (ctx) return ctx;
    return {
        palette: DEFAULT_PALETTE,
        mode: DEFAULT_MODE,
        resolvedMode: 'light',
        isDark: false,
        canChoose: true,
        globalTheme: GLOBAL_FALLBACK,
        setPalette: () => {},
        setMode: () => {},
        toggleMode: () => {},
        setGlobalTheme: () => {},
    };
};

export default ThemeContext;
